/**
 * State gate for IntersectionObserver-backed pagination.
 *
 * A failed page must stay quiescent while its sentinel remains in view. Only
 * an explicit Retry action may clear the failure; a successful page may arm
 * the observer again for the following offset.
 *
 * Each start returns an ownership lease for the current catalog generation.
 * reset() retires that lease immediately, so a new search/filter intent can
 * start its own page while the old request is still settling. Old finally
 * blocks cannot release the new generation's request or re-arm its observer.
 */
export function createPageRetryGate() {
  let failed = false;
  let generation = 0;
  let sequence = 0;
  let activeLease = null;
  const owns = (lease) => activeLease === lease && lease?.generation === generation;

  return {
    start({ explicit = false } = {}) {
      if ((failed && !explicit) || activeLease) return null;
      if (explicit) failed = false;
      activeLease = Object.freeze({ generation, request: ++sequence });
      return activeLease;
    },
    owns,
    fail(lease) {
      if (owns(lease)) failed = true;
    },
    succeed(lease) {
      if (owns(lease)) failed = false;
    },
    finish(lease) {
      if (!owns(lease)) return false;
      activeLease = null;
      return true;
    },
    reset() {
      generation += 1;
      activeLease = null;
      failed = false;
    },
    shouldRearm(hasMore) {
      return hasMore === true && !failed && !activeLease;
    },
    get loading() { return activeLease !== null; },
    get failed() { return failed; },
  };
}
